import React from 'react'

import { F0Box, F0Text } from '@factorialco/f0-react'
import { Widget } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'
import { formatDuration } from 'modules/ticketing/lib/formatDuration'

type ResolverEntry = {
  employeeId: string
  employeeName: string
  resolvedCount: number
  averageResolutionTimeSeconds: number
  slaCompliancePercent: number
}

type Props = {
  data: ResolverEntry[]
}

type CellProps = {
  content: string
  width: string
  align?: 'left' | 'right'
  variant?: 'label' | 'body'
  color?: 'default' | 'secondary'
}

const Cell: React.FC<CellProps> = ({
  content,
  width,
  align = 'left',
  variant = 'body',
  color = 'default',
}) => (
  <div style={{ width, flexShrink: 0 }}>
    <F0Box
      paddingX="md"
      paddingY="sm"
      display="flex"
      justifyContent={align === 'right' ? 'end' : 'start'}
    >
      <F0Text variant={variant} color={color} content={content} />
    </F0Box>
  </div>
)

const COLUMNS = {
  name: '40%',
  resolved: '20%',
  avgTime: '20%',
  sla: '20%',
} as const

export const InsightsResolverTable: React.FC<Props> = ({ data }) => {
  if (data.length === 0) {
    return (
      <Widget header={{ title: i18n.t('ticketing.inbox.insights.resolver_table.title') }}>
        <F0Box paddingX="md" paddingY="md">
          <F0Text
            variant="body"
            color="secondary"
            content={i18n.t('ticketing.inbox.insights.resolver_table.empty_description')}
          />
        </F0Box>
      </Widget>
    )
  }

  return (
    <Widget header={{ title: i18n.t('ticketing.inbox.insights.resolver_table.title') }}>
      <F0Box display="flex" flexDirection="column">
        {/* Header */}
        <F0Box display="flex" flexDirection="row" borderBottom="default">
          <Cell
            content={i18n.t('ticketing.inbox.insights.resolver_table.employee')}
            width={COLUMNS.name}
            variant="label"
            color="secondary"
          />
          <Cell
            content={i18n.t('ticketing.inbox.insights.resolver_table.tickets_resolved')}
            width={COLUMNS.resolved}
            align="right"
            variant="label"
            color="secondary"
          />
          <Cell
            content={i18n.t('ticketing.inbox.insights.resolver_table.avg_time')}
            width={COLUMNS.avgTime}
            align="right"
            variant="label"
            color="secondary"
          />
          <Cell
            content={i18n.t('ticketing.inbox.insights.resolver_table.sla_compliance')}
            width={COLUMNS.sla}
            align="right"
            variant="label"
            color="secondary"
          />
        </F0Box>

        {/* Rows */}
        {data.map((resolver, index) => (
          <F0Box
            key={resolver.employeeId}
            display="flex"
            flexDirection="row"
            borderBottom={index < data.length - 1 ? 'default' : undefined}
          >
            <Cell content={resolver.employeeName} width={COLUMNS.name} />
            <Cell content={String(resolver.resolvedCount)} width={COLUMNS.resolved} align="right" />
            <Cell
              content={formatDuration(resolver.averageResolutionTimeSeconds)}
              width={COLUMNS.avgTime}
              align="right"
            />
            <Cell content={`${resolver.slaCompliancePercent}%`} width={COLUMNS.sla} align="right" />
          </F0Box>
        ))}
      </F0Box>
    </Widget>
  )
}
